/**
 * hey404's Meta Pixel on the platform, loaded only on the checkout pages: the
 * pixel measures sales for the ads, not what students do in their courses.
 * Same pixel as the landing (apps/landing/src/lib/metaPixel.ts).
 *
 * Purchase carries the order id as its event id. When the API also sends the
 * purchase from the server (Conversions API, UNB2-267) with that same id, Meta
 * counts the two as one sale.
 */
const metaPixelId = "1449534637136556";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/** Meta's loader, as in its snippet: queue calls until fbevents.js arrives. */
function loadPixel(): Fbq {
  if (window.fbq) return window.fbq;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq ??= fbq;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
  fbq("init", metaPixelId);
  fbq("track", "PageView");
  return fbq;
}

type Money = { amountMinor: number; currency: string };

const value = ({ amountMinor, currency }: Money) => ({
  value: amountMinor / 100,
  currency: currency.toUpperCase(),
});

/** The buyer reached the checkout with a priced cart. */
export function trackInitiateCheckout(money: Money, slugs: string[]) {
  loadPixel()("track", "InitiateCheckout", {
    ...value(money),
    content_ids: slugs,
    content_type: "product",
    num_items: slugs.length,
  });
}

/**
 * The order is paid. Sent once per order and browser: reloading the success
 * page doesn't count the sale again.
 */
export function trackPurchase(orderId: string, money: Money, slugs: string[]) {
  const sentKey = `hey404-pixel-purchase-${orderId}`;
  try {
    if (localStorage.getItem(sentKey)) return;
    localStorage.setItem(sentKey, "1");
  } catch {
    // Storage blocked: send anyway; Meta drops the repeat by its event id.
  }
  loadPixel()(
    "track",
    "Purchase",
    {
      ...value(money),
      content_ids: slugs,
      content_type: "product",
      num_items: slugs.length,
    },
    { eventID: orderId },
  );
}
